import type { IntervalDataType } from '@flipster/r230522/original/interval/interval-key';

import { ResolutionString, Timezone } from '../../lib/charting_library';
import { defaultChartPriceType, normalizeIntervalDataType, spotChartPriceType } from './chart-price-type';

export const getTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone as Timezone;

export const delayCall = (fn: () => void) => setTimeout(fn, 0);

export const resolutionToSecs = (resolution: ResolutionString): number => {
  if (resolution === '1M') return 30 * 24 * 60 * 60;
  if (resolution === '1W') return 7 * 24 * 60 * 60;
  if (resolution === '1D') return 24 * 60 * 60;
  return Number(resolution) * 60;
};

export const secsToResolution = (secs: number): ResolutionString => {
  if (secs === 30 * 24 * 60 * 60) return '1M' as ResolutionString;
  if (secs === 7 * 24 * 60 * 60) return '1W' as ResolutionString;
  if (secs === 24 * 60 * 60) return '1D' as ResolutionString;
  return `${secs / 60}` as ResolutionString;
};

/**
 * 주어진 Unix 타임스탬프(초)를 월요일 00:00:00으로 내림합니다.
 */
export function floorToMonday(unixSec: number): number {
  const date = new Date(unixSec * 1000);
  const dayOfWeek = date.getUTCDay();

  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  date.setUTCHours(0, 0, 0, 0);
  date.setUTCDate(date.getUTCDate() - daysToSubtract);

  return Math.floor(date.getTime() / 1000);
}

/**
 * 주어진 Unix 타임스탬프(초)를 해당 월의 1일 00:00:00으로 내림합니다.
 */
export function floorToMonthStart(unixSec: number): number {
  const date = new Date(unixSec * 1000);

  date.setUTCDate(1);
  date.setUTCHours(0, 0, 0, 0);

  return Math.floor(date.getTime() / 1000);
}

export const getTimeByResolution = (timestamp: number, resolutionString: ResolutionString): number => {
  const nowSec = Math.floor(timestamp / 1000);

  if (resolutionString === '1W') {
    return floorToMonday(nowSec) * 1000;
  }

  if (resolutionString === '1M') {
    return floorToMonthStart(nowSec) * 1000;
  }

  const resolution = resolutionToSecs(resolutionString);
  const resolutionMs = resolution * 1000;
  return Math.floor(timestamp / resolutionMs) * resolutionMs;
};

export const createCompositeSymbolName = (symbolName: string, priceType?: IntervalDataType) => {
  if (symbolName.toLowerCase() === 'dummy') return symbolName;
  return `${symbolName}:${priceType ?? defaultChartPriceType}`;
};

export const symbolNameToInfo = (symbolName: string): { symbol: string; priceType: IntervalDataType } => {
  const [symbol, rawPriceType] = symbolName.split(':') as [string, IntervalDataType?];
  return { symbol, priceType: normalizeIntervalDataType(rawPriceType, symbol) };
};

export const sanitizeSymbolName = (symbolName: string) => {
  const { symbol, priceType } = symbolNameToInfo(symbolName);
  return `${symbol}:${priceType}`;
};

/**
 * Webview chart composite symbol.
 * Spot always uses BBO mid (`:MID`) — native mid is often sent with no suffix, which must not become LAST.
 * Perp keeps the normalized price type from the hash (MID/MARK — feature-era `:LAST`도 MID로 normalize됨).
 * ROLLBACK(last-price): 원본 — perp는 hash의 LAST/MARK 유지.
 */
export const toWebviewChartSymbol = (symbolName: string, isSpot?: boolean): string => {
  if (!symbolName || symbolName.toLowerCase() === 'dummy') return symbolName || 'dummy';
  const { symbol, priceType } = symbolNameToInfo(symbolName);
  const useSpotMid = isSpot ?? !symbol.endsWith('.PERP');
  return createCompositeSymbolName(symbol, useSpotMid ? spotChartPriceType : priceType);
};

export const safeCallAction = (action: (...args: unknown[]) => unknown) => {
  try {
    action();
  } catch {
    // 차트 내부 tvChart.current?.activeChart() 에러 발생
    // https://github.com/tradingview/charting_library/issues/5801
  }
};
